import { ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getROILink } from "../../../../utils/helpers";
import useCurrentClientProfile from "../../../../hooks/useCurrentClient";
import Navbar from "../../../../components/Navbar/Navbar";

const BILLING_CONTACT_EMAIL = "billing@humantold.com";
const BILLING_CONTACT_PHONE = "212.828.7473 x2";
const BOOK_INTAKE_LINK = "https://humantold.com/book-intake-appointment";
const CLIENT_EXPERIENCE_EMAIL = "client.experience@humantold.com";
const OUR_SERVICES_LINK = "https://humantold.com/our-offerings";
const RECORD_OF_DISCLOSURE_POLICY_LINK =
    "https://humantoldmanagement-help.freshdesk.com/en/support/solutions/articles/151000141006-record-of-disclosure-policy";

function ResourceSection({
    title,
    children,
    id,
}: {
    title: string;
    children: ReactNode;
    id?: string;
}) {
    return (
        <section
            id={id}
            className="rounded-r16 border border-[#E1E4EA] bg-white p-20 shadow-sm"
        >
            <h2 className="text-lg font-semibold text-black">{title}</h2>
            <div className="mt-10 space-y-10 text-sm leading-6 text-black">
                {children}
            </div>
        </section>
    );
}

export default function SessionResourcesPage() {
    const currentProfile = useCurrentClientProfile();
    const { hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const el = document.getElementById(hash.slice(1)) as HTMLDivElement;

            if (el) {
                el.scrollIntoView({ behavior: "instant" });
            }
        }
    }, [hash]);

    return (
        <>
            <Navbar title="Session resources" />
            <div className="page px-32 pb-32">
                <div className="rounded-r16 bg-[linear-gradient(189deg,_#F4F9FD_18.83%,_#1A738D_346.17%)] p-24">
                    <h1 className="mt-12 text-xxl font-semibold text-black">
                        Session resources
                    </h1>
                    <p className="mt-8 max-w-[760px] text-sm text-muted">
                        Key details for appointments, forms, billing, and
                        support in one place.
                    </p>
                </div>

                <div className="mt-24 grid gap-16">
                    <ResourceSection title="Consent form">
                        <p>
                            Consent forms must be completed before a session
                            begins. Please review the form carefully and return
                            accurate information.
                        </p>
                        <p>
                            Some services may require extra consent steps,
                            including additional forms for minors in family
                            therapy.
                        </p>
                    </ResourceSection>

                    <ResourceSection title="Fees">
                        <p>
                            Fees can vary by service and session type. We accept
                            credit cards and HSA/FSA cards.
                        </p>
                        <p>
                            If you are using commercial insurance, the billing
                            team may follow up with insurance-related details.
                        </p>
                        <p>
                            For billing questions, insurance changes, or fee
                            concerns, contact{" "}
                            <a
                                href={`mailto:${BILLING_CONTACT_EMAIL}`}
                                className="font-medium text-primary underline underline-offset-2"
                            >
                                {BILLING_CONTACT_EMAIL}
                            </a>{" "}
                            or {BILLING_CONTACT_PHONE}.
                        </p>
                    </ResourceSection>

                    <ResourceSection
                        id="cancellation-policy"
                        title="Cancellations / running late"
                    >
                        <p className="text-sm">
                            If you need to cancel/reschedule a single
                            appointment or are running late, please communicate
                            directly with your provider. If you ever wish to
                            cancel all ongoing appointments or be matched up
                            with another therapist, please contact us at{" "}
                            <a
                                href={`mailto:${CLIENT_EXPERIENCE_EMAIL}`}
                                className="font-medium text-primary underline underline-offset-2"
                            >
                                {CLIENT_EXPERIENCE_EMAIL}
                            </a>{" "}
                            or 212.828.7473 x1.
                        </p>
                        <p className="text-sm">
                            <div className="font-semibold">
                                Cancellation Policy for Therapy Services:{" "}
                            </div>
                            Cancellation We require a 24-hour cancellation
                            notice. It is occasionally necessary for everyone to
                            cancel an appointment. If you must cancel an
                            appointment, notice should be given 24-hours prior
                            to the time of the session. For a Monday
                            appointment, notice should be given by the
                            proceeding Friday. You will not be charged for
                            canceled sessions as long as you adhere to this
                            policy. Insurance Companies do not pay for missed or
                            cancelled sessions, therefore you will be
                            responsible for paying the $100 cancellation fee
                            out-of-pocket if less than 24-hours&apos; notice has
                            been given.
                        </p>

                        <p className="text-sm">
                            <div className="font-semibold">
                                Cancellation Policy for Medication Management
                                and Psychiatric Counseling:{" "}
                            </div>
                            We require a 24-hour cancellation notice. It is
                            occasionally necessary for everyone to cancel an
                            appointment. If you must cancel an appointment,
                            notice should be given 24-hours prior to the time of
                            the session. For Monday appointments, notice should
                            be given by the proceeding Friday. You will not be
                            charged for canceled sessions as long as you adhere
                            to this policy. Insurance companies do not pay for
                            missed or canceled sessions, therefore you will be
                            responsible for paying the $200 cancellation fee if
                            less than 24-hours&apos; notice has been given.
                        </p>
                        <p className="text-sm">
                            <div className="font-semibold">
                                Cancellation Policy for Testing/Psychological
                                Assessments:{" "}
                            </div>
                            We require a cancellation notice for all testing
                            related appointments. It is occasionally necessary
                            for everyone to cancel an appointment. If you must
                            cancel an appointment, notice should be given prior
                            to the time of the session per the guidelines below.
                            You will not be charged for canceled sessions as
                            long as you adhere to this policy. Insurance
                            Companies do not pay for missed or cancelled
                            sessions, therefore you will be responsible for
                            paying the cancellation fee out-of-pocket if less
                            notice than required has been given.
                            <div className="flex flex-col gap-y-2 mt-12">
                                <p>
                                    Intake Session: 7-day cancellation notice
                                    required, $200 cancellation fee
                                </p>
                                <p>
                                    Testing Session: 48-hours cancellation
                                    notice required, $300 cancellation fee
                                </p>
                                <p>
                                    Feedback Session: 48-hours cancellation
                                    notice required, $200 cancellation fee
                                </p>
                            </div>
                        </p>
                    </ResourceSection>

                    <ResourceSection title="Release of information">
                        <p>
                            Use a release form if you want a parent, guardian,
                            or another person to access billing or appointment
                            details on your behalf.
                        </p>
                        {currentProfile?.practice.practice_name ? (
                            <div>
                                <a
                                    href={getROILink(
                                        currentProfile?.practice.practice_name
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="shrink-0 text-xs font-medium text-primary underline"
                                >
                                    View ROI form
                                </a>
                            </div>
                        ) : null}
                    </ResourceSection>

                    <ResourceSection title="Out-of-Network (OON) Insurance Information">
                        <p>
                            We are an Out-of-Network (OON) provider. This means
                            we do not have contracts with insurance companies.
                            Rather than accepting insurance directly, we work
                            with your insurance company to help you maximize any
                            available out-of-network benefits.
                        </p>
                        <p>
                            As a courtesy, we will submit claims directly to
                            your insurance company on your behalf. Because we
                            are not contracted with your insurance carrier, any
                            reimbursement for covered services will be sent
                            directly to you, not to our practice.
                        </p>
                        <p>
                            Our team will monitor your claims and notify you
                            when your reimbursement has been issued. Once you
                            receive the reimbursement check, please deposit it
                            into your bank account and submit the corresponding
                            payment through our secure online client portal.
                        </p>
                        <p>
                            If we have not received your reimbursement payment
                            within four business days of notifying you that the
                            reimbursement has been issued, we will charge the
                            card on file for the outstanding balance unless you
                            have contacted us to make other arrangements.
                        </p>
                        <p>
                            If you have any questions about this process, your
                            Care Coordinator is happy to help.
                        </p>
                    </ResourceSection>

                    <ResourceSection title="Record of disclosure fees">
                        <p>
                            Humantold charges a fee for its Record of Disclosure
                            service. If you have questions about this service or
                            any other aspect of the practice, please contact us
                            for clarification and assistance. We are always
                            delighted to provide clarification and assistance
                            whenever needed
                        </p>
                        <p>
                            For fee and policy details,{" "}
                            <a
                                href={RECORD_OF_DISCLOSURE_POLICY_LINK}
                                target="_blank"
                                rel="noreferrer"
                                className="font-medium text-primary underline underline-offset-2"
                            >
                                view the Record of Disclosure policy
                            </a>
                            .
                        </p>
                    </ResourceSection>

                    <ResourceSection title="Our services">
                        <p>
                            We offer additional services beyond therapy to
                            support your mental health journey.
                        </p>
                        <p>
                            Explore available services at{" "}
                            <a
                                href={OUR_SERVICES_LINK}
                                target="_blank"
                                rel="noreferrer"
                                className="font-medium text-primary underline underline-offset-2"
                            >
                                humantold.com/our-offerings
                            </a>
                            .
                        </p>
                        <p>
                            If you want to learn more or schedule a call, use{" "}
                            <a
                                href={BOOK_INTAKE_LINK}
                                target="_blank"
                                rel="noreferrer"
                                className="font-medium text-primary underline underline-offset-2"
                            >
                                this link
                            </a>
                            .
                        </p>
                    </ResourceSection>

                    <ResourceSection title="Other questions">
                        <p>
                            If you have any questions or concerns, feel free to
                            reach out.
                        </p>
                    </ResourceSection>
                </div>
            </div>
        </>
    );
}
